import { Vector3 } from 'three'

// Camera constraint configuration
export const CAMERA_CONSTRAINTS = {
  // Distance Constraints (in meters)
  distance: {
    min: 5, // Much closer zoom for detailed inspection
    max: 300, // Further out for complete overview
    default: 80, // Starting distance for overview
  },

  // Vertical Angle Constraints (radians)
  polarAngle: {
    min: 0.1, // ~6° - Prevents looking through ground
    max: Math.PI * 0.4, // ~72° - Keeps horizon visible
    default: Math.PI * 0.25, // ~45° - Optimal viewing angle
  },

  // Horizontal Rotation (radians)
  azimuthAngle: {
    min: -Infinity, // Full 360° rotation allowed
    max: Infinity, // Unrestricted horizontal rotation
    default: (215 * Math.PI) / 180, // 215° azimuth starting view
  },

  // Pan Boundaries (world units, facility ~100x100m)
  pan: {
    x: { min: -30, max: 30 }, // East-West boundaries
    y: { min: 0, max: 50 }, // Height (no underground)
    z: { min: -30, max: 30 }, // North-South boundaries
  },

  // Target/Focus Point Constraints
  target: {
    x: { min: -20, max: 20 }, // Where camera looks at
    y: { min: 0, max: 15 }, // Keep focus on equipment level
    z: { min: -20, max: 20 }, // Centered on facility
  },
}

// Mouse control specifications
export const MOUSE_CONTROLS = {
  // Button Mapping
  buttons: {
    rotate: 0, // Left click + drag
    pan: 2, // Right click + drag
    zoom: 'wheel', // Mouse wheel
  },

  // Sensitivity Settings
  sensitivity: {
    rotate: 0.5, // Rotation speed
    pan: 1.0, // Pan speed
    zoom: 1.0, // Zoom speed
  },

  // Damping for Smooth Movement
  damping: {
    enabled: true,
    factor: 0.08, // Inertia strength (0-1)
  },

  // Zoom Behavior
  zoomBehavior: {
    scaleFactor: 0.95, // Zoom speed multiplier
    towardCursor: true, // Zoom toward mouse position
  },
}

// POI collider configuration for hover detection
export interface POIColliderConfig {
  size: { x: number; y: number; z: number }
  offset: { x: number; y: number; z: number }
  rotation?: number // Y-axis rotation in degrees
  excludedMeshes?: string[]
}

export const DEFAULT_COLLIDER: POIColliderConfig = {
  size: { x: 8, y: 4, z: 8 },
  offset: { x: 0, y: 2, z: 0 },
  rotation: 0,
}

// Point of Interest (POI) definitions
export interface POI {
  id: string
  name: string
  description: string
  position: { x: number; y: number; z: number }
  target: { x: number; y: number; z: number }
  offset?: { x: number; y: number; z: number }
  icon?: string
  constraints?: {
    minDistance?: number
    maxDistance?: number
    maxPolarAngle?: number
    panLimit?: number
  }
  collider?: POIColliderConfig
}

export const POI_CAMERAS: POI[] = [
  {
    id: 'overview',
    name: 'Full Facility',
    description: 'Complete plant overview',
    position: { x: 80, y: 50, z: 80 },
    target: { x: 0, y: 0, z: 0 },
    icon: '🏭',
    constraints: {
      minDistance: 60,
      maxDistance: 200,
    },
  },
  {
    id: 'batteryBank1',
    name: 'Battery Bank A',
    description: 'Primary battery storage',
    position: { x: -15, y: 12, z: -10 },
    target: { x: -20, y: 2, z: -15 },
    icon: '🔋',
    constraints: {
      minDistance: 5,
      maxDistance: 30,
      maxPolarAngle: Math.PI * 0.35,
    },
  },
  {
    id: 'batteryBank2',
    name: 'Battery Bank B',
    description: 'Secondary battery storage',
    position: { x: 15, y: 12, z: -10 },
    target: { x: 20, y: 2, z: -15 },
    icon: '🔋',
    constraints: {
      minDistance: 5,
      maxDistance: 30,
      maxPolarAngle: Math.PI * 0.35,
    },
  },
  {
    id: 'controlCenter',
    name: 'Control Center',
    description: 'Operations control',
    position: { x: 0, y: 15, z: 20 },
    target: { x: 0, y: 3, z: 10 },
    icon: '🖥️',
    constraints: {
      minDistance: 8,
      maxDistance: 35,
    },
  },
  {
    id: 'transformerYard',
    name: 'Transformer Yard',
    description: 'Power conversion',
    position: { x: 30, y: 20, z: 0 },
    target: { x: 25, y: 3, z: 0 },
    icon: '⚡',
    constraints: {
      minDistance: 10,
      maxDistance: 45,
    },
  },
  {
    id: 'gridConnection',
    name: 'Grid Connection',
    description: 'Main grid tie-in',
    position: { x: 40, y: 25, z: 20 },
    target: { x: 35, y: 5, z: 15 },
    icon: '🔌',
    constraints: {
      minDistance: 15,
      maxDistance: 60,
    },
  },
]

// Transition settings for camera movement
export const TRANSITION_SETTINGS = {
  duration: {
    min: 1.0, // Quick transitions
    default: 2.0, // Standard transitions
    max: 3.0, // Long distance transitions
  },

  easing: 'power2.inOut', // GSAP easing

  // Calculate duration based on distance
  getDuration: (currentPos: Vector3, targetPos: Vector3) => {
    const distance = currentPos.distanceTo(targetPos)
    const normalized = distance / CAMERA_CONSTRAINTS.distance.max
    return (
      TRANSITION_SETTINGS.duration.min +
      normalized * (TRANSITION_SETTINGS.duration.max - TRANSITION_SETTINGS.duration.min)
    )
  },
}
